import { find, filter, sortBy } from 'lodash'
import compact from 'lodash/compact'
import { formatMessage } from '../../fhg/utils/Utils'
import { getUserData } from '../../Utils/ServerUtil'
import get from 'lodash/get'

/**
 * Create the description based on available properties.
 *
 * @param evalItem The evaluation item to create the description for.
 * @return {string} The description.
 */
export const getEquipmentDescription = (evalItem) => {
    if (evalItem) {
        const descriptionArray = [
            evalItem.year && evalItem.year !== 'unknown'
                ? evalItem.year
                : undefined,
            evalItem.make && evalItem.make !== 'unknown'
                ? evalItem.make
                : undefined,
            evalItem.model && evalItem.model !== 'unknown'
                ? evalItem.model
                : undefined,
            evalItem.description && evalItem.description !== 'unknown'
                ? (evalItem.description?.value || evalItem.description)
                : undefined,
        ]
        const description = compact(descriptionArray).join(' ')
        return description
    }
    return ''
}

export const getIdDescription = (intl, evalItem) => {
    const idArray = [
        evalItem.serial && evalItem.serial !== 'unknown'
            ? formatMessage(
                  intl,
                  'equipmentList.serialNo.label',
                  'Serial:',
                  evalItem
              )
            : undefined,
        evalItem.vin && evalItem.vin !== 'unknown'
            ? formatMessage(intl, 'equipmentList.vin.label', 'VIN:', evalItem)
            : undefined,
        evalItem.personal_id && evalItem.personal_id !== 'unknown'
            ? formatMessage(
                  intl,
                  'equipmentList.inventoryId.label',
                  'Inventory ID:',
                  evalItem
              )
            : undefined,
        evalItem.sticker_id && evalItem.sticker_id !== 'unknown'
            ? formatMessage(
                  intl,
                  'equipmentList.stickerId.label',
                  'Sticker ID:',
                  evalItem
              )
            : undefined,
    ]
    return compact(idArray)
}

export const getYourEstimate = (evalItem, estimateType) => {
    const estimates = get(evalItem, 'estimates', [])
    const user_uuid = getUserData().uuid
    const filterObject = { user_uuid, status: 'current|estimate' }
    if (estimateType) {
        filterObject.estimate_type_name = estimateType.name
    }
    const estimateFiltered = sortBy(filter(estimates, filterObject), 'modified')
    return estimateFiltered.length > 0
        ? estimateFiltered[estimateFiltered.length - 1]
        : {}
}

export const getEstimatesByType = (evalItem, estimateType) => {
    const estimates = get(evalItem, 'estimates', [])
    const filterObject = {
        estimate_type_name: estimateType.name,
        status: 'current|estimate',
    }
    return sortBy(filter(estimates, filterObject), 'modified')
}

export const getConsensusEstimate = (evalItem, estimateType) => {
    const consensusValues = get(evalItem, 'consensus_values', [])
    const consensusValuesFiltered = filter(consensusValues, {
        name: estimateType.name,
    })
    return consensusValuesFiltered.length > 0
        ? consensusValuesFiltered[consensusValuesFiltered.length - 1]
        : {}
}

export const getYourEstimateFromEvaluation = (evaluation, itemIndex) => {
    const evalItem = get(evaluation, `items[${itemIndex}]`)
    return getYourEstimate(evalItem)
}

/**
 * Get the component response from IMS form details.
 *
 * @param imsFormDetails The IMS form details object.
 * @param groupSelector The group selector (title string or index).
 * @param componentSelector The component selector (label string or index).
 * @return {string} The component response or 'N/A'.
 */
export const getIMSComponentResponse = (
    imsFormDetails,
    groupSelector,
    componentSelector
) => {
    const groups = imsFormDetails?.ims_forms_data?.groups || []

    let group
    if (typeof groupSelector === 'string') {
        group = groups.find((g) => g.title === groupSelector)
    } else {
        group = groups[groupSelector]
    }

    if (!group) return 'N/A'

    let component
    if (typeof componentSelector === 'string') {
        component = group.components?.find(
            (c) =>
                c.label === componentSelector ||
                c.label_display === componentSelector ||
                (c.label && c.label.includes(componentSelector))
        )
    } else {
        component = group.components?.[componentSelector]
    }

    return component?.component_response || 'N/A'
}

/**
 * Get the meter value from the asset.
 *
 * @param asset The asset to get the meter from.
 * @param type The type of meter to get.
 * @return {string} The meter value.
 */
export const getMeterFromAsset = (asset, type) => {
    const item = asset.item || asset
    const meters =
        (typeof item.meters === 'string'
            ? JSON.parse(item.meters)
            : item.meters) || []
    const meter = find(meters, { units: type })
    return meter ? meter.value : ''
}

/**
 * Get other meters from the asset.
 *
 * @param asset The asset to get the meters from.
 * @return {Array} The other meters.
 */
export const getOtherMetersFromAsset = (asset) => {
    const item = asset.item || asset
    const meters =
        (typeof item.meters === 'string'
            ? JSON.parse(item.meters)
            : item.meters) || []
    return filter(
        meters,
        (meter) => meter.units !== 'Hours' && meter.units !== 'Miles'
    )
}

/**
 * Get more information from the asset.
 *
 * @param asset The asset to get the information from.
 * @return {string} The more information.
 */
export const getMoreInformationFromAsset = (asset) => {
    const item = asset.item || asset
    return item.notes || ''
}
